//#region Imports

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UserCourseService } from '../../shared/services/user-course/user-course.service';
import { UserCourse, UserCourseFilters } from '../../shared/models/interfaces/user-course.interface';
import { CourseCardComponent } from '../../shared/components/course-card/course-card.component';
import { Course } from '../../shared/models/interfaces/course.interface';
import { ToastService } from '../../shared/components/toast/service/toast.service';
import { UserCoursesFilterComponent } from './components/filter/filter.component';
import { UserCoursesPillsComponent, UserCoursesTabPill, UserCoursesTabValue } from './components/pills/pills.component';
import { CarouselComponent } from '../../shared/components/carousel/carousel.component';
import { CourseType } from '../../shared/models/enums/course-type.enum';
import { CourseLocation } from '../../shared/models/enums/course-location.enum';
import { UserCoursesTab } from '../../shared/models/enums/user-courses-tab.enum';
import { UserCoursesFilter } from '../../shared/models/interfaces/my-courses-filter.interface';
import { MeService } from '../../shared/services/me/me.service';
import { Category } from '../../shared/models/interfaces/category.interface';
import { CategoryService } from '../../shared/services/category/category.service';
import { getCrudErrors } from '../../shared/utils/functions';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';

//#endregion

@Component({
  selector: 'l-user-courses',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CourseCardComponent,
    CarouselComponent,
    UserCoursesFilterComponent,
    UserCoursesPillsComponent,
    SpinnerComponent
  ],
  templateUrl: './courses.component.html',
})
export class UserCoursesComponent implements OnInit {

  //#region Constructor

  constructor(
    private readonly userCourseService: UserCourseService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly categoryService: CategoryService,
    private readonly toastService: ToastService,
    private readonly meService: MeService,
  ) {}

  //#endregion

  //#region Public Properties

  public searchQuery: string = '';

  public selectedCategories: Category[] = [];

  public selectedType: CourseType | null = null;

  public selectedLocation: CourseLocation | null = null;

  public categories: Category[] = [];

  public activeTab: UserCoursesTab = UserCoursesTab.IN_PROGRESS;

  public tabFilterEnum: typeof UserCoursesTab = UserCoursesTab;

  public inProgressCourses: UserCourse[] = [];

  public readyToStartCourses: UserCourse[] = [];

  public completedCourses: UserCourse[] = [];

  public inProgressCount: number = 0;

  public readyToStartCount: number = 0;

  public completedCount: number = 0;

  public isLoadingCourses: boolean = false;

  public get tabPills(): UserCoursesTabPill[] {
    return [
      {
        label: 'Continue aprendendo',
        value: UserCoursesTab.IN_PROGRESS,
        count: this.inProgressCount,
      },
      {
        label: 'Prontos para começar',
        value: UserCoursesTab.READY_TO_START,
        count: this.readyToStartCount,
      },
      {
        label: 'Concluídos',
        value: UserCoursesTab.COMPLETED,
        count: this.completedCount,
      },
    ];
  }

  //#endregion

  //#region Getters

  private get userId(): string {
    return (this.activatedRoute.snapshot.paramMap.get('userId')
      ?? this.meService.getMe()?.id)
      ?? '';
  }

  //#endregion

  //#region Lifecycle Hooks

  public ngOnInit(): void {
    void Promise.all([
      this.loadCourses(),
      this.loadCategories(),
    ]);
  }

  //#endregion

  //#region Public Methods

  public onTabChange(tab: UserCoursesTabValue): void {
    this.activeTab = tab as UserCoursesTab;
    void this.loadCourses();
  }

  public applyFilters(filter: UserCoursesFilter): void {
    this.selectedCategories = filter.categories ?? [];
    this.selectedType = filter.type;
    this.selectedLocation = filter.location;
    this.searchQuery = filter.searchTerm;
    void this.loadCourses();
  }

  public get filteredCourses(): Course[] {
    return this.getActiveCourses()
      .map((uc) => {
        const progress = this.activeTab === UserCoursesTab.COMPLETED
          ? 100
          : uc.progress;

        return ({ ...uc.course!, progress }) as Course;
      });
  }

  private getActiveCourses(): UserCourse[] {
    switch (this.activeTab) {
      case UserCoursesTab.IN_PROGRESS: return this.inProgressCourses;
      case UserCoursesTab.READY_TO_START: return this.readyToStartCourses;
      case UserCoursesTab.COMPLETED: return this.completedCourses;
      default: return [];
    }
  }

  //#endregion

  //#region Private Methods

  private async loadCourses(): Promise<void> {
    this.isLoadingCourses = true;

    try {
      const filters = this.buildFilters();
      const [inProgress, readyToStart, completed] = await Promise.all([
        this.userCourseService.getInProgressCourses(this.userId, filters),
        this.userCourseService.getReadyToStartCourses(this.userId, filters),
        this.userCourseService.getCompletedCourses(this.userId, filters),
      ]);

      this.inProgressCourses = inProgress;
      this.readyToStartCourses = readyToStart;
      this.completedCourses = completed;

      this.inProgressCount = inProgress.length;
      this.readyToStartCount = readyToStart.length;
      this.completedCount = completed.length;
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    } finally {
      this.isLoadingCourses = false;
    }
  }

  private buildFilters(): Omit<UserCourseFilters, 'tab'> {
    return {
      searchTerm: this.searchQuery,
      categoryIds: this.selectedCategories.map(category => category.id),
      type: this.selectedType,
      location: this.selectedLocation,
    };
  }

  private async loadCategories(): Promise<void> {
    try {
      this.categories = await this.categoryService.search({
        search: { isActive: true },
        sort: [{ field: 'name', order: 'ASC' }]
      });
    } catch {
      this.toastService.error('Erro ao carregar categorias', 'Oops...');
    }
  }

  //#endregion
}
